import { useIsAdmin, useIsJobillan, useOpenOrganisation } from '@/hooks'
import { organisationIndexQueryKey, useOrganisationAcceptInvitation, useOrganisationIndex } from '@jobilla/nexus-sdk'
import { keepPreviousData, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, CreateOrganizationForm, FormDialog } from '../../ui'

export interface OrganizationSwitcherProps {
	currentSlug: string
}

export function OrganizationSwitcher({ currentSlug }: OrganizationSwitcherProps) {
	const { t } = useTranslation(['common', 'auth'])
	const openOrganisation = useOpenOrganisation()
	const queryClient = useQueryClient()
	const [open, setOpen] = useState(false)
	const [search, setSearch] = useState('')
	const [debouncedSearch, setDebouncedSearch] = useState('')
	const [acceptingId, setAcceptingId] = useState<string | null>(null)

	useEffect(() => {
		const timer = setTimeout(() => setDebouncedSearch(search), 300)
		return () => clearTimeout(timer)
	}, [search])

	const { data: organisations, isPlaceholderData } = useOrganisationIndex(
		debouncedSearch ? { 'filter[name]': debouncedSearch } : undefined,
		{ placeholderData: keepPreviousData, enabled: open },
	)
	const containerRef = useRef<HTMLDivElement>(null)
	const isAdmin = useIsAdmin()
	const isJobillan = useIsJobillan()

	// Resolve current org from the unfiltered cache so it stays stable during search
	const currentOrg = organisations?.find((org) => org.slug === currentSlug)
	const activeOrgs = organisations?.filter((org) => org.membershipStatus === 'active') ?? []
	const pendingOrgs = organisations?.filter((org) => org.membershipStatus === 'pending' && org.membershipId) ?? []

	const acceptMutation = useOrganisationAcceptInvitation({
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: organisationIndexQueryKey() })
		},
	})

	const clearSearch = () => {
		setSearch('')
		setDebouncedSearch('')
	}

	const handleSelect = async (org: { slug: string; workosId: string | null }) => {
		setOpen(false)
		clearSearch()
		await openOrganisation(org)
	}

	const handleAccept = (invitationId: string) => {
		setAcceptingId(invitationId)
		acceptMutation.mutate({ invitationId }, {
			onSettled: () => setAcceptingId(null),
		})
	}

	const [showCreateOrganization, setShowCreateOrganization] = useState(false)

	return (
		<div
			ref={containerRef}
			className="relative"
			onBlur={(e) => {
				if (!containerRef.current?.contains(e.relatedTarget)) {
					setOpen(false)
					clearSearch()
				}
			}}
		>
			<button
				type="button"
				onClick={() => setOpen(!open)}
				className="flex items-center gap-2 px-2 py-1 rounded-md text-sm text-grey-700 hover:bg-grey-100 transition-colors cursor-pointer"
			>
				<span className="w-6 h-6 rounded-full bg-gradient-to-r from-indigo-600 to-brand-600 flex items-center justify-center text-white text-xs font-semibold shrink-0">
					{currentOrg?.name.charAt(0).toUpperCase() ?? '?'}
				</span>
				<span className="max-w-[120px] truncate">
					{currentOrg?.name ?? currentSlug}
				</span>
				<svg
					className={`w-4 h-4 text-grey-400 transition-transform ${open ? 'rotate-180' : ''}`}
					fill="none"
					viewBox="0 0 24 24"
					stroke="currentColor"
					strokeWidth={2}
				>
					<path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
				</svg>
			</button>

			<FormDialog
				open={showCreateOrganization}
				onClose={() => setShowCreateOrganization(false)}
				title=""
			>
				<CreateOrganizationForm
					onCreated={async (organisation) => {
						await handleSelect(organisation)
						setShowCreateOrganization(false)
					}}
				/>
			</FormDialog>

			{open
				? (
					<div className="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-lg border border-grey-200 py-1 min-w-[240px] z-[9999]">
						<div className="px-3 py-2 border-b border-grey-100">
							<input
								type="text"
								value={search}
								onChange={(e) => setSearch(e.target.value)}
								placeholder={t('nav.searchOrganizations', 'Search organizations...')}
								className="w-full px-2.5 py-1.5 text-sm rounded-md border border-grey-200 text-grey-700 placeholder-grey-400 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
								autoFocus={true}
							/>
						</div>

						<div
							className={`max-h-[300px] overflow-y-auto transition-opacity duration-150 ${
								isPlaceholderData ? 'opacity-50' : 'opacity-100'
							}`}
						>
							{activeOrgs.length === 0 && pendingOrgs.length === 0 && debouncedSearch
									&& !isPlaceholderData
								? (
									<div className="px-3 py-3 text-sm text-grey-400 text-center">
										{t('nav.noSearchResults', 'No organizations found')}
									</div>
								)
								: null}

							{activeOrgs.length > 0 && (
								<>
									<div className="px-3 py-1.5 text-xs font-medium text-grey-400 uppercase tracking-wider">
										{t('nav.switchOrganization', 'Switch organization')}
									</div>
									{activeOrgs.map((org) => {
										const isCurrent = org.slug === currentSlug
										return (
											<button
												key={org.id}
												type="button"
												onClick={() => !isCurrent && void handleSelect(org)}
												className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm transition-colors text-left ${
													isCurrent
														? 'bg-grey-50 text-grey-700 font-medium cursor-default'
														: 'text-grey-700 hover:bg-grey-50 cursor-pointer'
												}`}
											>
												<span className="w-6 h-6 rounded-full bg-gradient-to-r from-indigo-600 to-brand-600 flex items-center justify-center text-white text-xs font-semibold shrink-0">
													{org.name.charAt(0).toUpperCase()}
												</span>
												<span className="truncate">{org.name}</span>
												{isCurrent
													? (
														<span className="ml-auto px-1.5 py-0.5 text-xs bg-indigo-100 text-indigo-800 rounded-full shrink-0">
															{t('nav.current', 'Current')}
														</span>
													)
													: null}
											</button>
										)
									})}
								</>
							)}

							{pendingOrgs.length > 0 && (
								<>
									<div className="px-3 py-1.5 text-xs font-medium text-grey-400 uppercase tracking-wider">
										{t('nav.pendingInvitations', 'Pending invitations')}
									</div>
									{pendingOrgs.map((org) => {
										const isAccepting = acceptingId === org.membershipId
										return (
											<div
												key={org.id}
												className="flex items-center gap-2.5 px-3 py-2 text-sm"
											>
												<span className="w-6 h-6 rounded-full bg-grey-300 flex items-center justify-center text-white text-xs font-semibold shrink-0">
													{org.name.charAt(0).toUpperCase()}
												</span>
												<span className="truncate flex-1 text-grey-500">{org.name}</span>
												<button
													type="button"
													disabled={isAccepting}
													onClick={() => handleAccept(org.membershipId!)}
													className="shrink-0 px-2.5 py-1 rounded-full text-xs font-medium bg-indigo-600 text-white hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
												>
													{isAccepting
														? t('nav.joining', 'Joining...')
														: t('nav.join', 'Join')}
												</button>
											</div>
										)
									})}
								</>
							)}
						</div>

						{isAdmin || isJobillan
							? (
								<div className="px-3 py-2 border-t border-grey-100">
									<Button
										type="colorful"
										size="tiny"
										fullWidth={true}
										onClick={() => setShowCreateOrganization(true)}
									>
										{t('auth:createOrganization.submit')}
									</Button>
								</div>
							)
							: null}
					</div>
				)
				: null}
		</div>
	)
}
